import React, { useState, useEffect } from 'react';
import { X, Save, FileText } from 'lucide-react';
import { getAllScripts, saveScript } from '../../../database/db';

/**
 * Script Editor Component
 * Allows editing and managing call script templates
 *
 * @param {boolean} show - Whether to show the editor
 * @param {function} onClose - Callback when editor is closed
 */
const ScriptEditor = ({ show, onClose }) => {
    const [scripts, setScripts] = useState([]);
    const [selectedScript, setSelectedScript] = useState(null);
    const [scriptName, setScriptName] = useState('');
    const [scriptContent, setScriptContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show) {
            loadScripts();
        }
    }, [show]);

    const loadScripts = () => {
        const allScripts = getAllScripts();
        setScripts(allScripts);

        // Select default script
        if (allScripts.length > 0) {
            selectScript(allScripts[0]);
        }
    };

    const selectScript = (script) => {
        setSelectedScript(script);
        setScriptName(script.name);
        setScriptContent(script.content);
        setError('');
    };

    const handleSave = async () => {
        if (!scriptName.trim()) {
            setError('Script name is required');
            return;
        }

        if (!scriptContent.trim()) {
            setError('Script content is required');
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            const result = saveScript(
                scriptName.trim(),
                scriptContent.trim(),
                selectedScript?.type || 'general',
                selectedScript?.id
            );

            if (result.success) {
                loadScripts();
                alert('Script saved successfully!');
            } else {
                throw new Error(result.error || 'Failed to save script');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                            <FileText size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Call Script Editor</h2>
                            <p className="text-sm text-purple-100">Manage script templates</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                        title="Close"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex">
                    {/* Script List Sidebar */}
                    <div className="w-64 border-r border-gray-200 overflow-y-auto bg-gray-50">
                        <div className="p-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                Script Templates
                            </h3>
                            <div className="space-y-2">
                                {scripts.map((script) => (
                                    <button
                                        key={script.id}
                                        onClick={() => selectScript(script)}
                                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                                            selectedScript?.id === script.id
                                                ? 'bg-purple-600 text-white'
                                                : 'bg-white text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <div className="font-semibold text-sm">{script.name}</div>
                                        <div
                                            className={`text-xs mt-1 ${
                                                selectedScript?.id === script.id
                                                    ? 'text-purple-100'
                                                    : 'text-gray-500'
                                            }`}
                                        >
                                            {script.type}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Editor Panel */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {selectedScript ? (
                            <div className="space-y-4">
                                {/* Script Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Script Name
                                    </label>
                                    <input
                                        type="text"
                                        value={scriptName}
                                        onChange={(e) => {
                                            setScriptName(e.target.value);
                                            setError('');
                                        }}
                                        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                                    />
                                </div>

                                {/* Script Content */}
                                <div className="flex-1">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Script Content
                                    </label>
                                    <textarea
                                        value={scriptContent}
                                        onChange={(e) => {
                                            setScriptContent(e.target.value);
                                            setError('');
                                        }}
                                        rows={15}
                                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none font-mono text-sm resize-none"
                                        placeholder="Enter your call script here..."
                                    />
                                </div>

                                {/* Available Placeholders */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-blue-800 mb-2">
                                        Available Placeholders
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-blue-700">
                                        <code>{'{name}'}</code>
                                        <code>{'{your_name}'}</code>
                                        <code>{'{company}'}</code>
                                        <code>{'{email}'}</code>
                                        <code>{'{phone}'}</code>
                                        <code>{'{material_type}'}</code>
                                        <code>{'{frequency}'}</code>
                                        <code>{'{number_of_boxes}'}</code>
                                        <code>{'{quoted}'}</code>
                                        <code>{'{boxes}'}</code>
                                        <code>{'{date_of_service}'}</code>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-2">
                                        These placeholders will be automatically replaced with actual
                                        lead data when the script is displayed.
                                    </p>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                        <p className="text-sm text-red-700">{error}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                <p>Select a script to edit</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || !selectedScript}
                        className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {isSaving ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={20} />
                                Save Script
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScriptEditor;
